import React from 'react'

import PropTypes from 'prop-types'
import { Box, Button, Tooltip, Typography, useTheme } from '@material-ui/core'
import clsx from 'clsx'
import { Plus as PlusIcon, Info as InfoIcon } from 'react-feather'

import { BaseDialog } from 'components'
import {
  ColumnAddForm,
  ColumnDeleteDialog,
  ColumnList,
  FinalizedDatesSection,
} from './components'

import useColumnManagement from 'hooks/useColumnManagement'

import constants from 'constants/index'
import useStyles from './styles'

const CUSTOM_COLUMN_LIMIT = 9

const ColumnManagementModal = ({
  open,
  setOpen,
  columns,
  boardId,
  onSave,
  canManageColumns,
  canDestroyColumn,
  onConfigurationSaved,
}) => {
  const { KANBAN_COLUMN_ROLE_FINALIZED, KANBAN_COLUMN_ROLE_CUSTOM } =
    constants.activities

  const classes = useStyles()
  const theme = useTheme()

  const {
    editingId,
    isDeleting,
    localColumns,
    showAddForm,
    setShowAddForm,
    confirmDeleteId,
    setConfirmDeleteId,
    editData,
    editNameRef,
    finalizedDatesRef,
    atLimit,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    setEditData,
    handleAddColumn,
    handleDragEnd,
    handleCloseConfirmDelete,
    handleConfirmDelete,
    handleSave,
  } = useColumnManagement({
    open,
    columns,
    boardId,
    onSave,
    setOpen,
    KANBAN_COLUMN_ROLE_FINALIZED,
    KANBAN_COLUMN_ROLE_CUSTOM,
    CUSTOM_COLUMN_LIMIT,
  })

  return (
    <>
      <BaseDialog
        open={open}
        setOpen={setOpen}
        title={
          canManageColumns ? 'Gerenciar Colunas' : 'Gerenciar Datas Finalizadas'
        }
        dialogSize="md"
        fullWidth
        closeButtonText="Cancelar"
        actionButtonText="Salvar"
        actionButton={handleSave}
        paperStyle={{
          borderRadius: 12,
          border: 'none',
          borderTop: `4px solid ${theme.palette.primary.main}`,
          overflow: 'hidden',
        }}
      >
        {canManageColumns && (
          <>
            <Box className={classes.tableHeader}>
              <Box />
              <Typography className={classes.headerCell}>Ordem</Typography>
              <Typography className={classes.headerCell}>Nome</Typography>
              <Typography className={classes.headerCell}>Cor fundo</Typography>
              <Box
                display="flex"
                alignItems="center"
                className={classes.headerGap}
              >
                <Typography className={classes.headerCell}>
                  Cor texto
                </Typography>
                <Tooltip title="As cores de fundo e texto são visíveis apenas no modo lista">
                  <span className={classes.infoIconWrapper}>
                    <InfoIcon size={12} />
                  </span>
                </Tooltip>
              </Box>
              <Typography
                className={clsx(classes.headerCell, classes.headerActionsCell)}
              >
                Ações
              </Typography>
            </Box>

            <ColumnList
              columns={localColumns}
              editingId={editingId}
              editData={editData}
              editNameRef={editNameRef}
              onStartEdit={handleStartEdit}
              onCancelEdit={handleCancelEdit}
              onSaveEdit={handleSaveEdit}
              onEditColorChange={(color) =>
                setEditData((prev) => ({ ...prev, color }))
              }
              onEditTextColorChange={(textColor) =>
                setEditData((prev) => ({ ...prev, textColor }))
              }
              onDeleteClick={setConfirmDeleteId}
              onDragEnd={handleDragEnd}
              canManageColumns={canManageColumns}
              canDestroyColumn={canDestroyColumn}
            />

            {showAddForm && (
              <ColumnAddForm
                columnCount={localColumns.length}
                onAdd={handleAddColumn}
                onCancel={() => setShowAddForm(false)}
              />
            )}

            {!showAddForm && (
              <Tooltip
                title={
                  atLimit
                    ? `Limite máximo de ${CUSTOM_COLUMN_LIMIT} colunas personalizadas atingido`
                    : ''
                }
              >
                <span>
                  <Button
                    startIcon={<PlusIcon size={14} />}
                    className={classes.addButton}
                    onClick={() => setShowAddForm(true)}
                    size="small"
                    disabled={atLimit}
                  >
                    Nova Coluna
                  </Button>
                </span>
              </Tooltip>
            )}
          </>
        )}

        <FinalizedDatesSection
          ref={finalizedDatesRef}
          onSaved={onConfigurationSaved}
          standalone={!canManageColumns}
        />
      </BaseDialog>

      <ColumnDeleteDialog
        open={!!confirmDeleteId}
        onClose={handleCloseConfirmDelete}
        onConfirm={handleConfirmDelete}
        loading={isDeleting}
        accentColor={
          localColumns.find((col) => col.id === confirmDeleteId)?.color
        }
      />
    </>
  )
}

ColumnManagementModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  columns: PropTypes.array.isRequired,
  boardId: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  canManageColumns: PropTypes.bool,
  canDestroyColumn: PropTypes.bool,
  onConfigurationSaved: PropTypes.func,
}

export default ColumnManagementModal
